import {
  FALLBACK_LOCALE,
  type LanguageCode,
  type Locale,
  DEFAULT_LANGUAGE_CODE,
  isSupportedLocale,
  localeFromLoginCountry,
} from './locales'

const normalize = (value: string) => value.trim().toLowerCase().replace('_', '-')

export const normalizeLanguageCode = (value: unknown): LanguageCode => {
  if (typeof value !== 'string') {
    return DEFAULT_LANGUAGE_CODE
  }

  const normalized = normalize(value)
  if (!normalized) {
    return DEFAULT_LANGUAGE_CODE
  }

  if (normalized === 'system') {
    return 'system'
  }
  if (normalized === 'kr' || normalized.startsWith('ko')) {
    return 'ko'
  }
  if (normalized === 'jp' || normalized === 'jpn' || normalized.startsWith('ja')) {
    return 'ja'
  }
  if (normalized === 'eng' || normalized.startsWith('en')) {
    return 'en'
  }

  return DEFAULT_LANGUAGE_CODE
}

export const normalizeLocale = (value: unknown): Locale | null => {
  if (typeof value !== 'string') {
    return null
  }

  const normalized = normalize(value)
  if (!normalized) {
    return null
  }

  if (normalized === 'kr' || normalized.startsWith('ko')) {
    return 'ko'
  }
  if (normalized === 'jp' || normalized === 'jpn' || normalized.startsWith('ja')) {
    return 'ja'
  }
  if (normalized === 'eng' || normalized.startsWith('en')) {
    return 'en'
  }

  return isSupportedLocale(normalized) ? normalized : null
}

export const resolveLocaleFromAcceptLanguage = (acceptLanguage: string | null | undefined): Locale | null => {
  if (!acceptLanguage) {
    return null
  }

  return (
    acceptLanguage
      .split(',')
      .map((part, index) => {
        const [language, ...parameters] = part.split(';')
        const qualityParameter = parameters.find((parameter) => parameter.trim().toLowerCase().startsWith('q='))
        const parsedQuality = qualityParameter ? Number(qualityParameter.split('=')[1]) : 1

        return {
          index,
          locale: normalizeLocale(language),
          quality: Number.isFinite(parsedQuality) ? Math.min(Math.max(parsedQuality, 0), 1) : 0,
        }
      })
      .filter(({ locale, quality }) => locale !== null && quality > 0)
      .sort((left, right) => right.quality - left.quality || left.index - right.index)[0]?.locale ?? null
  )
}

/**
 * 표시 언어 결정. 신호가 강한 순서대로 본다.
 *
 * 1. i18n 킬스위치(off면 이유불문 ko)
 * 2. 사용자가 직접 고른 앱 언어 (`PODO_LANGUAGE_CODE`)
 * 3. 기기 언어 (`PODO_SYSTEM_LOCALE` → Accept-Language)
 * 4. 로그인 국가 (`PODO_LOGIN_COUNTRY`)
 * 5. en
 *
 * 국가가 기기 언어보다 뒤인 이유: 국가 선택은 로그인·결제 수단을 고르는 화면이지 언어를 고르는
 * 화면이 아니다. 기기 언어가 그 사람이 실제로 읽고 싶은 언어에 가장 가까우므로, 일본에서 쓰는
 * 한국어 사용자는 한국어로 본다. 국가는 기기 언어가 지원 목록 밖일 때 en 대신 쓰는 fallback 이다.
 */
export const resolveEffectiveLocale = ({
  languageCode,
  loginCountry,
  systemLocale,
  acceptLanguage,
  i18nEnabled = true,
}: {
  languageCode?: unknown
  loginCountry?: unknown
  systemLocale?: string | null
  acceptLanguage?: string | null
  i18nEnabled?: boolean
}): Locale => {
  // ponytail: i18n 킬스위치 — off면 이유불문 ko. 로케일 결정의 단일 차단점.
  if (!i18nEnabled) {
    return 'ko'
  }

  const normalizedLanguageCode = normalizeLanguageCode(languageCode)

  if (normalizedLanguageCode !== 'system') {
    return normalizedLanguageCode
  }

  return (
    normalizeLocale(systemLocale) ??
    // systemLocale 은 단일 로케일일 수도, Accept-Language 헤더 원문일 수도 있다. 서버는
    // getServerLocaleContext 에서 `systemLocale ?? acceptLanguage` 를 provider 로 내려보내므로,
    // 여기서 목록 형태를 파싱하지 않으면 서버와 클라이언트가 서로 다른 locale 을 계산한다
    // (예: 'zh-CN,ko;q=0.8' → 서버 ko / 클라 en). 쿼리 키가 갈려 SSR 캐시가 버려진다.
    resolveLocaleFromAcceptLanguage(systemLocale) ??
    resolveLocaleFromAcceptLanguage(acceptLanguage) ??
    localeFromLoginCountry(loginCountry) ??
    FALLBACK_LOCALE
  )
}
